import { API_BASE_URL } from '../configuration/config';
import { BaseService } from './base-service';
import { StatusCodes } from 'http-status-codes';

export class ServiceError extends Error {
  constructor(message: string, public readonly statusCode: number) {
    super(message);
    this.name = 'ServiceError';
  }
}

export interface ConversationMessage {
  text: string;
  sender: "user" | "bot";
}

export interface ConversationSummary {
  conversationId: string;
  title: string;
  updatedAt: number;
  messageCount: number;
}

export enum MessageRole {
  USER = "user",
  ASSISTANT = "assistant",
}

export interface Conversation {
  conversationId: string;
  title: string;
  messages: Array<{ role: MessageRole; content: string; timestamp: number }>;
  updatedAt: number;
}

export interface SendMessageResponse {
  response: string;
  conversationMode: "persistent" | "created" | "stateless";
  conversationId?: string;
}

export interface QuickActionItem {
  id: string;
  title: string;
  prompt: string;
  createdAt?: number;
  updatedAt?: number;
}

export class ChatbotService extends BaseService {
  public static instance = new ChatbotService(API_BASE_URL);

  public async sendMessage(
    message: string,
    conversationHistory: ConversationMessage[],
    conversationId?: string
  ): Promise<SendMessageResponse> {
    const body: Record<string, unknown> = { message, conversationHistory };
    if (conversationId) {
      body.conversationId = conversationId;
    }

    const response = await this.fetch('/chatbot/query', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (response.status !== StatusCodes.OK) {
      throw new ServiceError(
        `Chatbot request failed with status ${response.status}`,
        response.status
      );
    }

    return await response.json();
  }

  public async getConversations(nextToken?: string): Promise<{ conversations: ConversationSummary[]; nextToken?: string }> {
    const params = new URLSearchParams();
    if (nextToken) {
      params.set('nextToken', nextToken);
    }
    const path = `/chatbot/conversations${params.toString() ? `?${params.toString()}` : ''}`;

    const response = await this.fetch(path, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.status !== StatusCodes.OK) {
      throw new ServiceError(
        `Failed to fetch conversations with status ${response.status}`,
        response.status
      );
    }

    return await response.json();
  }

  public async getConversation(conversationId: string): Promise<Conversation> {
    const response = await this.fetch(`/chatbot/conversations/${conversationId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.status !== StatusCodes.OK) {
      throw new ServiceError(
        `Failed to fetch conversation with status ${response.status}`,
        response.status
      );
    }

    return await response.json();
  }

  public async createConversation(): Promise<{ conversationId: string; title: string; updatedAt: number; messageCount: number }> {
    const response = await this.fetch('/chatbot/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.CREATED) {
      throw new ServiceError(
        `Failed to create conversation. Response status: ${response.status}`,
        response.status
      );
    }

    return await response.json();
  }

  public async deleteConversation(conversationId: string): Promise<void> {
    const response = await this.fetch(`/chatbot/conversations/${conversationId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.status !== StatusCodes.OK && response.status !== StatusCodes.NO_CONTENT) {
      throw new ServiceError(
        `Failed to delete conversation with status ${response.status}`,
        response.status
      );
    }
  }

  public async getQuickActions(): Promise<QuickActionItem[]> {
    const response = await this.fetch('/chatbot/quick-actions', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.status !== StatusCodes.OK) {
      throw new ServiceError(
        `Failed to fetch quick actions. Response status: ${response.status}`,
        response.status
      );
    }

    const data = await response.json();
    return data.items as QuickActionItem[];
  }

  public async saveQuickActions(items: QuickActionItem[]): Promise<QuickActionItem[]> {
    const response = await this.fetch('/chatbot/quick-actions', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items.map((item) => ({ id: item.id, title: item.title, prompt: item.prompt })) }),
    });

    if (response.status !== StatusCodes.OK) {
      throw new ServiceError(
        `Failed to save quick actions. Response status: ${response.status}`,
        response.status
      );
    }

    const data = await response.json();
    return data.items as QuickActionItem[];
  }
}
